import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import type {
  AnswerProfile,
  DemographicProfile,
  SegmentOverlap,
  SurveySegment,
} from 'src/types/focus-group.types';

export const LOW_COVERAGE_THRESHOLD = 0.5;
export const HIGH_OVERLAP_JACCARD = 0.25;
export const MIN_SURVEY_AI_RESPONDENTS = 100;

export const SIMULATED_RESPONDENT_LABEL =
  'Simulated composite respondent — not a real person or new survey data.';

export function formatCoveragePct(coveragePct: number): string {
  return `${Math.round(coveragePct * 100)}%`;
}

export function formatSegmentShare(size: number, totalAiRespondents: number): string {
  if (totalAiRespondents <= 0) {
    return `${size} AI respondents`;
  }
  const share = Math.round((size / totalAiRespondents) * 100);
  return `${size} (${share}% of AI respondents)`;
}

export function hasLowCoverage(coveragePct: number): boolean {
  return coveragePct < LOW_COVERAGE_THRESHOLD;
}

export function hasHighOverlap(overlap: SegmentOverlap): boolean {
  return Object.values(overlap).some((entry) => entry.jaccard >= HIGH_OVERLAP_JACCARD);
}

export function buildCoverageCautionMessage(coveragePct: number): string {
  const coverage = formatCoveragePct(coveragePct);
  return `These segments cover ${coverage} of AI respondents; most don't fall into any segment.`;
}

export function buildOverlapCautionMessage(): string {
  return 'Segments overlap heavily — treat boundaries as approximate, not mutually exclusive groups.';
}

export function summariseDemographics(profile: DemographicProfile): string[] {
  return Object.entries(profile).slice(0, 3).map(([attribute, values]) => {
    const top = Object.entries(values).sort((a, b) => b[1].share - a[1].share)[0];
    if (!top) {
      return attribute;
    }
    const [value, stats] = top;
    return `${attribute}: ${value} (${Math.round(stats.share * 100)}%)`;
  });
}

export function summariseDefiningAnswers(
  segment: SurveySegment,
  questions: CombinedQuestionStats[]
): string[] {
  const lines: string[] = [];
  const profile = segment.answer_profile as AnswerProfile;

  Object.entries(profile).forEach(([questionId, answers]) => {
    const question = questions.find((item) => item.id === questionId);
    const topAnswer = Object.entries(answers).sort((a, b) => b[1].share - a[1].share)[0];
    if (!topAnswer) {
      return;
    }
    const [answerId, stats] = topAnswer;
    const answerTitle = question?.answers.find((answer) => answer.id === answerId)?.title
      ?? (answerId === '__free_text__' ? 'Free text responses' : 'Selected answer');
    const questionTitle = question?.title ?? 'Survey question';
    lines.push(`${questionTitle}: ${answerTitle} (${Math.round(stats.share * 100)}%)`);
  });

  return lines.slice(0, 4);
}

export function questionIndexForId(
  questionId: string,
  questions: CombinedQuestionStats[]
): number | null {
  const index = questions.findIndex((question) => question.id === questionId);
  return index >= 0 ? index : null;
}

export function isTurnCapError(message: string): boolean {
  return /turn limit/i.test(message);
}

export function isDailyCapError(message: string): boolean {
  return /daily message limit/i.test(message);
}
